import {useEffect} from 'react';
import {Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import './App.css';
import ProjectsPage from './pages/projects.page';
import TranscriptionsPage from './pages/transcriptions.container';
import WorkspacePage from './pages/workspace.page';
import BatchTranscribePage from './pages/batchTranscribePage';
import ConversionListPage from './pages/conversionList.page';
import WordListsPage from './pages/wordlists.page';
import TranscriptionQueuePage from './pages/transcriptionQueuePage';
import LoginPage from './pages/loginPage';
import WelcomePage from './pages/welcomePage';
import BatchConvertPage from './pages/batchConvertPage';
import LandingPage from './pages/landingPage';
import DocsPage from './pages/docsPage';
import TermsOfServicePage from './pages/termsOfServicePage';
import ContactPage from './pages/contactPage';
import DownloadsPage from './pages/downloadsPage';
import SignupPage from './pages/signup.page';
import AuthConfirmPage from './pages/authConfirm.page';
import AuthErrorPage from './pages/authError.page';
import ForgotPasswordPage from './pages/forgotPassword.page';
import ResetPasswordPage from './pages/resetPassword.page';
import SetPasswordPage from './pages/setPassword.page';
import AccountPage from './pages/accountPage';
import CheckoutPage from './pages/checkoutPage';
import AdminPage from './pages/admin.page';
import AdminUsersPage from './pages/adminUsers.page';
import AdminUserDetailPage from './pages/adminUserDetail.page';
import AdminUserConversionsPage from './pages/adminUserConversions.page';
import AdminSignupsPage from './pages/adminSignups.page';
import AdminEmailsPage from './pages/adminEmails.page';
import AdminQueuePage from './pages/adminQueue.page';
import AdminWordlistsPage from './pages/adminWordlists.page';
import AdminWordlistDetailPage from './pages/adminWordlistDetail.page';
import AdminRoute from './components/admin/AdminRoute';
import {ToastContainer} from './components/Toast';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import PaddleInitializer from './components/PaddleInitializer';
import TranscriptionPollerContainer from './components/transcriptionPoller.container';
import PendingTranscriptionsContainer from './components/pendingTranscriptions.container';
import VideoEditorGateContainer from './components/videoEditorGate.container';
import MarketingLayoutContainer from './components/marketingLayout.container';
import {useAppDispatch, useAppSelector} from './store/hooks';
import {debugLog} from './lib/debug';
import {readAuthRedirectErrorParams} from './lib/authRedirectError';
import {fetchProjects, loadProjectMetadata} from './store/slices/projectsThunks';
import {fetchUserProfile, initializeAuth, updateSession} from './store/slices/authThunks';
import {fetchSubscriptions} from './store/slices/subscriptionThunks';
import {fetchTranscriptionUsage} from './store/slices/usageThunks';
import {needsPasswordSetupSelector, userSelector} from './store/selectors/auth.selectors';
import {supabase} from './lib/supabaseClient';
import {getCurrentProjectId} from './lib/projectPreferences';

// Project routes wrapper to extract projectId from URL params
function ProjectRoutesWrapper() {
  const {projectId} = useParams<{projectId: string}>();
  const navigate = useNavigate();

  return (
    <Routes>
      <Route path="/" element={<Navigate to="workspace" replace />} />
      <Route path="/workspace" element={<WorkspacePage />} />
      {/*
        Transcribe and Convert were two halves of one job and are now one page. The URLs stay
        alive because they are in bookmarks and in the header the old build shipped.
      */}
      <Route path="/transcribe" element={<Navigate to="../workspace" replace />} />
      <Route path="/convert" element={<Navigate to="../workspace" replace />} />
      <Route
        path="/transcriptions"
        element={
          <TranscriptionsPage
            projectId={projectId}
            onNewTranscription={() => navigate(`/projects/${projectId}/workspace`)}
          />
        }
      />
      <Route path="/batch-transcribe" element={<BatchTranscribePage />} />
      <Route path="/batch-convert" element={<BatchConvertPage />} />
      <Route path="/queue" element={<TranscriptionQueuePage />} />
      <Route path="/wordlists" element={<WordListsPage />} />
      <Route path="/conversions" element={<ConversionListPage />} />
    </Routes>
  );
}

/**
 * Chrome for the signed-in app: the project header plus a fixed-height, non-scrolling shell.
 * Marketing pages deliberately do not use this — they scroll and carry their own header/footer.
 *
 * The transcription poller lives here so a running job keeps being watched wherever the user goes
 * in the app — it is explicitly safe to navigate away from the page that started one.
 */
function AppLayout() {
  return (
    <div className="bg-gray-900 h-screen w-full flex flex-col overflow-hidden">
      <TranscriptionPollerContainer />
      {/* Same reason as the poller: a job queued behind a session limit can be hours from its
          release, and the user will not be on the workspace when it comes due. */}
      <PendingTranscriptionsContainer />
      {/*
        Blocks every page under here — but not /set-password, which is deliberately outside
        AppLayout — until `video_editor` is set. Re-shown on every sign-in for as long as that's
        null, whether that's a brand-new account or an existing one that cleared its answer.
      */}
      <VideoEditorGateContainer />
      <Header />
      <div className="flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}

// Paths that already read a Supabase auth-redirect error out of the URL themselves, in context —
// the global effect below only fires for everything else.
const AUTH_ERROR_HANDLED_ELSEWHERE = new Set(['/login', '/auth/confirm', '/reset-password', '/auth/error']);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const needsPassword = useAppSelector(needsPasswordSetupSelector);

  // Initialize auth on mount and listen for auth state changes
  useEffect(() => {
    dispatch(initializeAuth());

    // Listen for auth state changes (login/logout/token refresh). On the web this also fires
    // after the Google OAuth redirect, once Supabase has exchanged the code in the URL.
    const {data: {subscription}} = supabase.auth.onAuthStateChange((event, session) => {
      debugLog('[App] Auth state changed:', event, session?.user?.email);
      // Update Redux store with the new session (or null)
      dispatch(updateSession(session));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  // Entitlement is read all over the app (the header plan badge, feature gating), so the billing
  // state is fetched once here rather than only on the pages that render pricing.
  // The header shows the transcription balance on every page, so the usage read belongs here
  // alongside billing rather than on the one page that spends it.
  // The set-password gate has to be global, not just on the app routes: the login link that
  // provisioning emails lands on whatever origin the backend redirects to, which is usually the
  // public landing page. Without this a brand-new account sits there signed in, with no password and
  // nothing telling it to choose one — `ProtectedRoute` would only catch it once it went looking for
  // the app. The state is transient by definition, so pulling the user out of a public page is fine.
  useEffect(() => {
    if (user && needsPassword && location.pathname !== '/set-password') {
      navigate('/set-password', {replace: true});
    }
  }, [user, needsPassword, location.pathname, navigate]);

  // Supabase reports an already-used or expired email link (or a rejected OAuth attempt) by
  // appending error params to whatever URL it redirects back to. `/login`, `/auth/confirm` and
  // `/reset-password` all already read those in place, but a recovery link built from the stock
  // `{{ .ConfirmationURL }}` template lands on the root landing page instead when its own
  // `redirect_to` isn't honoured — and until this effect existed, that page rendered right over the
  // error with nothing telling the visitor their link was dead. This is the global catch-all for
  // every path that doesn't already handle it itself.
  useEffect(() => {
    if (AUTH_ERROR_HANDLED_ELSEWHERE.has(location.pathname)) {
      return;
    }
    const {error, errorCode, description} = readAuthRedirectErrorParams(location.search, location.hash);
    if (!error && !errorCode && !description) {
      return;
    }
    const params = new URLSearchParams();
    if (description) {
      params.set('message', description);
    }
    navigate(`/auth/error${params.toString() ? `?${params.toString()}` : ''}`, {replace: true});
  }, [location.pathname, location.search, location.hash, navigate]);

  // The profile read is here too, and not only for display: `password_set` on that row is what
  // releases the set-password gate for an account provisioned from a checkout. The JWT claim covers
  // the moment before this lands; the row is the authority once it does.
  useEffect(() => {
    if (user) {
      dispatch(fetchUserProfile());
      dispatch(fetchSubscriptions());
      dispatch(fetchTranscriptionUsage());
    }
  }, [user, dispatch]);

  // Fetch projects when the user is authenticated and restore the last-used project
  useEffect(() => {
    if (!user) {
      return;
    }

    dispatch(fetchProjects()).then(async (result: any) => {
      if (result.type !== 'projects/fetch/fulfilled' || !Array.isArray(result.payload)) {
        if (result.type === 'projects/fetch/rejected') {
          console.error('Failed to fetch projects:', result.payload);
        }
        return;
      }

      // Load locally persisted favourites / lastActivity
      try {
        await dispatch(loadProjectMetadata()).unwrap();
      } catch (error) {
        console.error('Failed to load project metadata:', error);
        // Continue anyway - this is not critical
      }

      // Restore the last-selected project, if it still exists
      const savedProjectId = getCurrentProjectId();
      if (savedProjectId) {
        const savedProject = result.payload.find((p: any) => p.id === savedProjectId);
        if (savedProject) {
          dispatch({type: 'projects/setCurrent/fulfilled', payload: savedProject});
          return;
        }
      }

      // Fallback: the project flagged as the default, or the first one the user has. Leaving no
      // current project selected strands every project-scoped page without context.
      const fallbackProject = result.payload.find((p: any) => p.isDefault) ?? result.payload[0];
      if (fallbackProject) {
        dispatch({type: 'projects/setCurrent/fulfilled', payload: fallbackProject});
      }
    });
  }, [user, dispatch]);

  return (
    <>
      <ToastContainer />
      <PaddleInitializer />

      {/*
        One route table, three groups. Public pages must render for signed-out visitors — the app
        used to answer every URL with the login screen, which left no room for a landing page,
        docs, or the legal pages Paddle requires.
      */}
      <Routes>
        {/* Public — marketing chrome */}
        <Route element={<MarketingLayoutContainer />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Route>

        {/*
          Public — auth. Sign-in and sign-up are no longer one screen in two modes: signup is a
          payment flow that ends in an emailed link, and shares no fields with sign-in (it has no
          password box at all, because the account does not exist yet).
        */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        {/*
          Where every emailed token_hash/type link lands — the signup login link and the password
          recovery link both point here now, not directly at their next screen, because that format
          needs an explicit verifyOtp call rather than detectSessionInUrl's automatic code exchange.
          Public: there is no session until verifyOtp succeeds partway through rendering it.
        */}
        <Route path="/auth/confirm" element={<AuthConfirmPage />} />
        {/*
          Where a Supabase auth-redirect error lands when nothing more specific caught it first —
          see the auth-redirect effect below. Standalone like the other auth screens, not under
          MarketingLayoutContainer: AuthCard already draws its own full-screen shell and logo.
        */}
        <Route path="/auth/error" element={<AuthErrorPage />} />
        {/*
          Password reset. Both stay public even though the recovery link signs the user in on
          arrival (via /auth/confirm) — putting /reset-password behind ProtectedRoute would strand
          anyone whose session hasn't landed in the store yet.
        */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/*
          Protected, but deliberately outside AppLayout: an account provisioned from a checkout has
          no password yet, and this is the one thing it can do until it has one. Wrapping it in the
          app chrome would offer navigation that ProtectedRoute would immediately bounce back here.
        */}
        <Route
          path="/set-password"
          element={
            <ProtectedRoute>
              <SetPasswordPage />
            </ProtectedRoute>
          }
        />

        {/* Protected — the app itself */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />

          {/* Projects landing page */}
          <Route path="/projects" element={<ProjectsPage />} />

          {/* Project-specific routes */}
          <Route path="/projects/:projectId/*" element={<ProjectRoutesWrapper />} />

          {/* Default-project twins (the project flagged isDefault) */}
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/transcribe" element={<Navigate to="/workspace" replace />} />
          <Route path="/convert" element={<Navigate to="/workspace" replace />} />
          <Route path="/queue" element={<TranscriptionQueuePage />} />
          <Route
            path="/transcriptions"
            element={<TranscriptionsPage onNewTranscription={() => navigate('/workspace')} />}
          />
          <Route path="/batch-transcribe" element={<BatchTranscribePage />} />
          <Route path="/batch-convert" element={<BatchConvertPage />} />
          <Route path="/wordlists" element={<WordListsPage />} />
          <Route path="/conversions" element={<ConversionListPage />} />

          {/*
            Admin. Protected like the rest of the app, and then gated again by AdminRoute — but the
            gate that counts is the server's: every /rest/v1/admin/* route answers a non-admin with
            404 rather than 403, so the section does not confirm it exists. AdminRoute turns the
            first such 404 into a redirect. The JWT's `censor_me_is_admin` claim only decides
            whether the header draws the link.
          */}
          <Route path="/admin" element={<AdminRoute />}>
            <Route index element={<AdminPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="users/:userId/conversions" element={<AdminUserConversionsPage />} />
            <Route path="signups" element={<AdminSignupsPage />} />
            <Route path="emails" element={<AdminEmailsPage />} />
            <Route path="transcriptions" element={<AdminQueuePage />} />
            <Route path="wordlists" element={<AdminWordlistsPage />} />
            <Route path="wordlists/:wordlistId" element={<AdminWordlistDetailPage />} />
          </Route>
        </Route>

        {/* Unknown path: signed-in users get the app, everyone else gets the landing page. */}
        <Route path="*" element={<Navigate to={user ? '/welcome' : '/'} replace />} />
      </Routes>
    </>
  );
}

export default App;
